/**
 * Alta y edicion de un cliente.
 *
 * LAS TRES DECISIONES DE ESTE FORMULARIO
 * --------------------------------------
 * 1. La pregunta del anclaje ocupa el lugar mas grande. Todo lo demas —nombre,
 *    telefono, direccion— se corrige cuando quieras. El anclaje, no.
 * 2. En la edicion de un cliente que ya opero, la pregunta no se deshabilita en
 *    silencio: se muestra el motivo, con los dos numeros que estarian en juego,
 *    y que camino queda.
 * 3. Antes de escribir hay un paso de revision, igual que en la caja. En el alta
 *    ese paso dice quien es y en que moneda queda su deuda, con la misma frase
 *    que la pregunta, porque es el ultimo momento en que se puede cambiar de
 *    opinion gratis. Nada mas: los datos que se acaban de escribir no hace falta
 *    leerlos de nuevo en una tabla.
 */
import { useEffect, useState } from "react";
import { ArrowLeft, Lock } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { Fila } from "@/components/caja/DialogMovimiento";
import { ChipMoneda, Importe, InputImporte } from "@/components/money";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { CambiosCliente, Cliente, NuevoCliente } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import { AnclajeBloqueado, CONSECUENCIA_ANCLAJE, PreguntaAnclaje } from "./ExplicadorAnclaje";
import {
  FORMULARIO_VACIO,
  anclajeDe,
  armarAlta,
  armarCambios,
  formularioConBuscado,
  formularioDesde,
  formularioListo,
  hayCambios,
  limiteNegativo,
  puedeCambiarAnclaje,
  type FormularioCliente,
  type ImpactoAnclaje,
} from "./logica";

export interface DialogClienteProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** `null` = alta. Con cliente = edicion. */
  cliente: Cliente | null;
  /**
   * En el alta, lo que ya se tipeo buscando al cliente: el nombre, o el
   * telefono si son solo numeros. Desde la venta el formulario abria vacio y
   * habia que escribirlo de nuevo.
   */
  inicial?: string;
  /** Movimientos en cuenta corriente. Con uno solo, el anclaje queda fijo. */
  movimientos?: number;
  /** Los dos numeros del mismo historial, para explicar el bloqueo. */
  impacto?: ImpactoAnclaje | null;
  cotizacion: number;
  guardando?: boolean;
  onCrear: (datos: NuevoCliente) => void;
  onActualizar: (cambios: CambiosCliente) => void;
}

export function DialogCliente({
  abierto,
  onAbiertoChange,
  cliente,
  inicial = "",
  movimientos = 0,
  impacto = null,
  cotizacion,
  guardando = false,
  onCrear,
  onActualizar,
}: DialogClienteProps): JSX.Element {
  const esAlta = cliente === null;
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  const [form, setForm] = useState<FormularioCliente>(FORMULARIO_VACIO);

  // Al abrir se carga el cliente o se arranca limpio. Un formulario que recuerda
  // el alta anterior termina dando de alta dos veces a la misma persona.
  useEffect(() => {
    if (!abierto) return;
    setPaso("carga");
    setForm(cliente ? formularioDesde(cliente) : formularioConBuscado(inicial));
  }, [abierto, cliente, inicial]);

  const anclajeEditable = esAlta || puedeCambiarAnclaje(movimientos);
  const cambios = cliente ? armarCambios(form, cliente, anclajeEditable) : {};
  const listo = formularioListo(form) && (esAlta || hayCambios(cambios));

  function poner<K extends keyof FormularioCliente>(campo: K, valor: FormularioCliente[K]): void {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function confirmar(): void {
    if (esAlta) {
      onCrear(armarAlta(form));
      return;
    }
    onActualizar(cambios);
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="lg">
        <DialogHeader>
          <DialogTitle>
            {paso === "revision"
              ? esAlta
                ? "Revisá antes de crear"
                : "Revisá antes de guardar"
              : esAlta
                ? "Cliente nuevo"
                : `Editar a ${cliente.nombre}`}
          </DialogTitle>
          {/* En el resumen del alta la bajada no suma nada que la tarjeta no diga:
              queda para el lector de pantalla, que si la necesita. */}
          <DialogDescription className={cn(paso === "revision" && esAlta && "sr-only")}>
            {paso === "revision"
              ? esAlta
                ? "Quién es y en qué moneda queda su deuda."
                : "Estos son los campos que se van a modificar."
              : esAlta
                ? "Hacen falta el nombre, el teléfono y elegir en qué moneda queda su deuda. El resto se completa cuando aparezca."
                : "Los datos de contacto y el límite se corrigen cuando quieras."}
          </DialogDescription>
        </DialogHeader>

        {paso === "carga" ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo label="Nombre" requerido className="sm:col-span-2">
                <Input
                  id="cli-nombre"
                  value={form.nombre}
                  onChange={(e) => poner("nombre", e.target.value)}
                  placeholder="Nombre y apellido, o el nombre del comercio"
                  autoComplete="off"
                  autoFocus
                />
              </Campo>
              <Campo label="Teléfono" requerido>
                <Input
                  id="cli-telefono"
                  inputMode="tel"
                  value={form.telefono}
                  onChange={(e) => poner("telefono", e.target.value)}
                  placeholder="3401 51-2001"
                  autoComplete="off"
                />
              </Campo>
              <Campo label="DNI o CUIT">
                <Input
                  id="cli-documento"
                  value={form.documento}
                  onChange={(e) => poner("documento", e.target.value)}
                  autoComplete="off"
                />
              </Campo>
              <Campo label="Dirección">
                <Input
                  id="cli-direccion"
                  value={form.direccion}
                  onChange={(e) => poner("direccion", e.target.value)}
                  autoComplete="off"
                />
              </Campo>
              <Campo label="Localidad">
                <Input
                  id="cli-localidad"
                  value={form.localidad}
                  onChange={(e) => poner("localidad", e.target.value)}
                  autoComplete="off"
                />
              </Campo>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              {anclajeEditable ? (
                <PreguntaAnclaje valor={form.anclaje} onChange={(a) => poner("anclaje", a)} />
              ) : (
                <div className="space-y-3">
                  <TituloSeccion nivel="seccion" as="h3" icono={Lock}>
                    Cuenta{" "}
                    {cliente && anclajeDe(cliente) === "DOLAR"
                      ? "anclada al dólar"
                      : "en pesos, congelada"}
                  </TituloSeccion>
                  <AnclajeBloqueado
                    movimientos={movimientos}
                    anclaje={cliente ? anclajeDe(cliente) : "PESOS"}
                    impacto={impacto}
                  />
                </div>
              )}
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <TituloSeccion
                nivel="seccion"
                as="h3"
                descripcion="Opcional. Cuando una venta a cuenta lo pasa, el sistema avisa antes de emitirla — no la bloquea. Dejalo vacío si no querés poner tope."
              >
                Límite de crédito
              </TituloSeccion>
              <InputImporte
                label="Tope"
                valor={form.limite}
                onValorChange={(v) => poner("limite", v)}
                moneda={form.limiteMoneda}
                onMonedaChange={(m: Moneda) => poner("limiteMoneda", m)}
                cotizacion={cotizacion}
                ayuda="Un límite sin moneda no dice nada: elegí en cuál está."
              />
              {limiteNegativo(form) ? (
                <p role="alert" className="text-xs text-destructive">
                  El límite no puede ser negativo.
                </p>
              ) : null}
            </div>

            <div className="border-t border-border pt-4">
              <Campo label="Observaciones">
                <Input
                  id="cli-obs"
                  value={form.observaciones}
                  onChange={(e) => poner("observaciones", e.target.value)}
                  placeholder="Lo que haya que recordar de este cliente"
                  autoComplete="off"
                />
              </Campo>
            </div>
          </div>
        ) : (
          <Revision
            form={form}
            esAlta={esAlta}
            cambios={cambios}
            anclajeEditable={anclajeEditable}
            cliente={cliente}
          />
        )}

        <DialogFooter>
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              <Button type="button" disabled={!listo} onClick={() => setPaso("revision")}>
                Revisar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button type="button" cargando={guardando} onClick={confirmar}>
                {/* Con el nombre: el boton dice a quien crea. */}
                {esAlta ? (
                  <span className="truncate">Crear a {form.nombre.trim()}</span>
                ) : (
                  "Guardar los cambios"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ETIQUETA_CAMPO: Record<string, string> = {
  nombre: "Nombre",
  telefono: "Teléfono",
  documento: "DNI o CUIT",
  email: "Email",
  direccion: "Dirección",
  localidad: "Localidad",
  observaciones: "Observaciones",
  limite_credito: "Límite de crédito",
  limite_moneda: "Moneda del límite",
  cc_modo: "Anclaje de la cuenta",
  cc_moneda: "Moneda de la cuenta",
};

function Revision({
  form,
  esAlta,
  cambios,
  anclajeEditable,
  cliente,
}: {
  form: FormularioCliente;
  esAlta: boolean;
  cambios: CambiosCliente;
  anclajeEditable: boolean;
  cliente: Cliente | null;
}): JSX.Element {
  const enDolar = form.anclaje === "DOLAR";

  if (esAlta) {
    const anclaje = enDolar ? "DOLAR" : "PESOS";
    const telefono = form.telefono.trim();
    return (
      <div className="space-y-3">
        <Card variant="muted" padding="normal" className="space-y-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{form.nombre.trim()}</p>
            <p className="flex flex-wrap items-baseline gap-x-1 text-xs text-muted-foreground">
              {telefono ? <span>{telefono} ·</span> : null}
              {form.limite !== null ? (
                <span>
                  límite de crédito{" "}
                  <Importe monto={form.limite} moneda={form.limiteMoneda} tamano="sm" />
                </span>
              ) : (
                <span>sin límite de crédito</span>
              )}
            </p>
          </div>

          <div className="flex items-start gap-3 border-t border-border pt-3">
            <ChipMoneda moneda={enDolar ? "USD" : "ARS"} className="mt-1" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">
                {enDolar ? "Su deuda queda en dólares" : "Su deuda queda en pesos"}
              </p>
              <p className="text-xs text-muted-foreground">{CONSECUENCIA_ANCLAJE[anclaje]}</p>
            </div>
          </div>
        </Card>

        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="size-3.5 shrink-0" aria-hidden="true" />
          Se puede cambiar hasta su primera venta a cuenta.
        </p>
      </div>
    );
  }

  const claves = Object.keys(cambios);

  return (
    <Card variant="muted" padding="normal" className="space-y-3">
      {claves.length === 0 ? (
        <p className="text-sm text-muted-foreground">No cambiaste nada.</p>
      ) : (
        <dl className="space-y-2 text-sm">
          {claves.map((clave) => {
            const valor = (cambios as Record<string, unknown>)[clave];
            return (
              <Fila
                key={clave}
                termino={ETIQUETA_CAMPO[clave] ?? clave}
                valor={
                  clave === "limite_credito" ? (
                    valor === null ? (
                      "sin tope"
                    ) : (
                      <Importe monto={Number(valor)} moneda={form.limiteMoneda} />
                    )
                  ) : (
                    String(valor)
                  )
                }
              />
            );
          })}
        </dl>
      )}

      {!anclajeEditable && cliente ? (
        <p className="text-xs text-muted-foreground">
          El anclaje de la cuenta no se toca: sigue{" "}
          {anclajeDe(cliente) === "DOLAR" ? "anclada al dólar" : "fija en pesos"}.
        </p>
      ) : null}
    </Card>
  );
}
